// IMPORTS

// external
import algosdk, {
  Algodv2,
  Transaction,
  getApplicationAddress,
  encodeUint64,
  SuggestedParams,
  assignGroupID,
  makeAssetTransferTxnWithSuggestedParamsFromObject,
  makeApplicationNoOpTxnFromObject,
  bytesToBigInt,
  bigIntToBytes
} from "algosdk"

// global
import { Base64Encoder } from "../../encoder"
import { getApplicationGlobalState, getLocalStates, getAccountBalances } from "../../stateUtils"
import { getParams, getPaymentTxn } from "../../transactionUtils"
import { STAKING_STRINGS } from "./stakingConfig"
import AlgofiUser from "../../algofiUser"
import { decodeBytes, parseAddressBytes, formatPrefixState } from "../../utils"
import { FIXED_12_SCALE_FACTOR, BANK_ASSET_ID } from "../../globals"

// asset data
import AssetDataClient from "../../assetData/assetDataClient"
import AssetAmount from "../../assetData/assetAmount"

// local
import StakingClient from "./stakingClient"
import StakingConfig from "./stakingConfig"
import RewardsProgramState from "./rewardsProgramState"

// STAKING QUOTE INTERFACE

export class StakingQuote {
  newAPR: number
  newBoost: number
  newAnnualBankPer1k: number
  newTotalAnnualBank: number
  
  constructor(newAPR: number, newBoost: number, newAnnualBankPer1k: number, newTotalAnnualBank: number) {
    this.newAPR = newAPR
    this.newBoost = newBoost
    this.newAnnualBankPer1k = newAnnualBankPer1k
    this.newTotalAnnualBank = newTotalAnnualBank
  }
}

// INTERFACE
export default class Staking {
  // static
  public algod: Algodv2
  public stakingClient: StakingClient
  public assetDataClient: AssetDataClient
  public name: string
  public appId: number
  public address: string
  public assetId: number
  public latestTime: number

  public rewardsEscrowAccount: string
  public votingEscrowAppId: number
  public totalStaked: number
  public scaledTotalStaked: number
  public rewardsManagerAppId: number
  public rewardsProgramCount: number
  public rewardsProgramStates: { [key: number]: RewardsProgramState }
  
  public baseAPR: number
  public annualBankPer1k: number

  /**
   * Constructor for the staking object. 
   * 
   * @param algod - algod client
   * @param stakingClient - staking client
   * @param rewardsManagerAppId - rewards manager app id
   * @param stakingConfig - stakingConfig object with information on the staking
   */
  constructor(algod: Algodv2, stakingClient: StakingClient, rewardsManagerAppId: number, stakingConfig: StakingConfig) {
    this.algod = algod
    this.stakingClient = stakingClient
    this.assetDataClient = stakingClient.algofiClient.assetData
    this.name = stakingConfig.name
    this.appId = stakingConfig.appId
    this.address = getApplicationAddress(this.appId)
    this.assetId = stakingConfig.assetId
    this.rewardsManagerAppId = rewardsManagerAppId
  }

  /**
   * Loads in the global state of the specific staking contract and sets relevant
   * fields on the class.
   */
  async loadState() {
    // loading in global state staking specific
    const globalState = await getApplicationGlobalState(this.algod, this.appId)

    this.latestTime = globalState[STAKING_STRINGS.latest_time]
    this.rewardsEscrowAccount = parseAddressBytes(globalState[STAKING_STRINGS.rewards_escrow_account])
    this.votingEscrowAppId = globalState[STAKING_STRINGS.voting_escrow_app_id]
    this.totalStaked = globalState[STAKING_STRINGS.total_staked]
    this.scaledTotalStaked = globalState[STAKING_STRINGS.scaled_total_staked]
    this.rewardsManagerAppId = globalState[STAKING_STRINGS.rewards_manager_app_id]
    this.rewardsProgramCount = globalState[STAKING_STRINGS.rewards_program_count]
    this.rewardsProgramStates = {}

    // loading in rewards program specific state
    const formattedState = formatPrefixState(globalState)
    this.baseAPR = 0
    this.annualBankPer1k = 0
    const loadingRewardProgramStates = []
    for (let i = 0; i < this.rewardsProgramCount; ++i) {
      if (!this.rewardsProgramStates[i]) {
        this.rewardsProgramStates[i] = new RewardsProgramState(this, formattedState, i)
      }
      loadingRewardProgramStates.push(this.rewardsProgramStates[i].loadState())
      if (this.rewardsProgramStates[i].rewardsAssetId == BANK_ASSET_ID) {
        this.annualBankPer1k += this.rewardsProgramStates[i].annualBaseRewardsPer1k
      } else {
        this.baseAPR += this.rewardsProgramStates[i].baseAPR
      }
    }
    await Promise.all(loadingRewardProgramStates)
  }

  getTotalStaked(): AssetAmount {
    return this.assetDataClient.getAsset(this.totalStaked, this.assetId)
  }

  getScaledTotalStaked(): AssetAmount {
    return this.assetDataClient.getAsset(this.scaledTotalStaked, this.assetId)
  }

  getStakingAPRQuote(user: AlgofiUser, amountToStake: number): StakingQuote {
    let userStakingState = user.staking.v2.userStakingStates[this.appId]
    let userVotingState = user.governance.v1.userVotingEscrowState
    let newUserTotalStaked =  userStakingState ? userStakingState.totalStaked + amountToStake : amountToStake
    let newUserScaledTotalStaked = userVotingState ? Math.min(newUserTotalStaked, (0.4 * newUserTotalStaked) + (0.6 * userVotingState.projBoostMultiplier * this.totalStaked / FIXED_12_SCALE_FACTOR)) : 0
    let newBoost = Math.max(1, newUserScaledTotalStaked / (0.4 * newUserTotalStaked)) || 1
    let newAPR = this.baseAPR * newBoost
    let newAnnualBankPer1k = this.annualBankPer1k * newBoost
    let newTotalAnnualBank = this.annualBankPer1k * newBoost * this.assetDataClient.getAsset(newUserTotalStaked, this.assetId).toUSD() / 1000
    return new StakingQuote(newAPR, newBoost, newAnnualBankPer1k, newTotalAnnualBank)
  }
  
  getLockingAPRQuote(user: AlgofiUser, newBoostMultiplier: number) {
    let userStakingState = user.staking.v2.userStakingStates[this.appId]
    let userTotalStaked = userStakingState ? userStakingState.totalStaked : 0
    let newUserScaledTotalStaked = Math.min(userTotalStaked, (0.4 * userTotalStaked) + (0.6 * newBoostMultiplier * this.totalStaked / FIXED_12_SCALE_FACTOR))
    let newBoost = (newUserScaledTotalStaked / (0.4 * userTotalStaked)) || 1
    let newAPR = this.baseAPR * newBoost
    let newAnnualBankPer1k = this.annualBankPer1k * newBoost
    let newTotalAnnualBank = this.annualBankPer1k * newBoost * this.assetDataClient.getAsset(userTotalStaked, this.assetId).toUSD() / 1000
    return new StakingQuote(newAPR, newBoost, newAnnualBankPer1k, newTotalAnnualBank)
  }

  /**
   * Constructs a series of transactions that opt a user into the staking
   * contract.
   * 
   * @param user - user who is opting in 
   * @returns a series of transactions that opt a user into the staking
   * contract.
   */
  async getUserOptInTxns(user: AlgofiUser): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()

    // unstake transaction
    const txn0 = algosdk.makeApplicationOptInTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      suggestedParams: params,
      appArgs: undefined,
      accounts: undefined,
      foreignApps: undefined,
      foreignAssets: undefined,
      rekeyTo: undefined
    })

    return [txn0]
  }

  /**
   * Constructs a series of transactions that opt a user into the staking
   * contract.
   * 
   * @param user - user who is opting in 
   * @returns a series of transactions that opt a user into the staking
   * contract.
   */
  async getUserCloseOutTxns(user: AlgofiUser): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()

    // unstake transaction
    const txn0 = algosdk.makeApplicationCloseOutTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      suggestedParams: params,
      appArgs: [encodeUint64(0)], // don't ignore unclaimed rewards
      accounts: undefined,
      foreignApps: undefined,
      foreignAssets: undefined,
      rekeyTo: undefined
    })

    return [txn0]
  }

  /**
   * Constructs a series of transactions to stake user's assets in the staking
   * contract.
   * 
   * @param user - user who is staking
   * @param amount - amount they are staking
   * @returns a series of transactions to stake user's assets in the staking
   * contract.
   */
  async getStakeTxns(user: AlgofiUser, amount: number): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()

    // farm ops
    const txn0 = makeApplicationNoOpTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      appArgs: [enc.encode(STAKING_STRINGS.farm_ops)],
      suggestedParams: params,
      accounts: undefined,
      foreignAssets: undefined,
      foreignApps: undefined,
      rekeyTo: undefined
    })

    // sending staking asset
    const txn1 = makeAssetTransferTxnWithSuggestedParamsFromObject({
      from: user.address,
      to: this.address,
      assetIndex: this.assetId,
      amount: amount,
      suggestedParams: params,
      rekeyTo: undefined,
      revocationTarget: undefined
    })

    // stake
    params.fee = 2000
    const txn2 = makeApplicationNoOpTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      appArgs: [enc.encode(STAKING_STRINGS.stake)],
      suggestedParams: params,
      foreignApps: [this.votingEscrowAppId || 1],
      accounts: undefined,
      foreignAssets: undefined,
      rekeyTo: undefined
    })

    return assignGroupID([txn0, txn1, txn2])
  }

  /**
   * Constructs a series of transactions that unstake a user's current stake
   * from the staking contract.
   * 
   * @param user - user who is unstaking
   * @param amount - amount they are unstaking
   * @returns a series of transactions that unstake a user's current stake
   * from the staking contract.
   */
  async getUnstakeTxns(user: AlgofiUser, amount: number): Promise<Transaction[]> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()

    // farm ops
    const txn0 = makeApplicationNoOpTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      appArgs: [enc.encode(STAKING_STRINGS.farm_ops)],
      suggestedParams: params,
      accounts: undefined,
      foreignAssets: undefined,
      foreignApps: undefined,
      rekeyTo: undefined
    })

    params.fee = 3000
    // unstake
    const txn1 = algosdk.makeApplicationNoOpTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      appArgs: [enc.encode(STAKING_STRINGS.unstake), encodeUint64(amount)],
      foreignAssets: [this.assetId],
      suggestedParams: params,
      foreignApps: [this.votingEscrowAppId || 1],
      accounts: undefined,
      rekeyTo: undefined
    })

    return assignGroupID([txn0, txn1])
  }

  /**
   * Constructs a series of transactions that claim a user's staked assets.
   * 
   * @param user - user who is claiming
   * @returns a series of transactions that claim a user's staked assets.
   */
  async getClaimTxns(user: AlgofiUser): Promise<any> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()

    const txns = []

    // iterate over all rewards programs on this contract
    for (let i = 0; i < this.rewardsProgramCount; ++i) {

      // skip rewards programs with 0 unrealized rewards
      if (user.staking.v2.userStakingStates[this.appId].userRewardsProgramStates[i].userUnrealizedRewards > 0) {
        
        // opt in if needed
        if (!user.isOptedInToAsset(this.rewardsProgramStates[i].rewardsAssetId)) {
          params.fee = 1000
          let assetOptInTxn = getPaymentTxn(params, user.address, user.address, this.rewardsProgramStates[i].rewardsAssetId, 0)
          assetOptInTxn.note = enc.encode("Asset Opt In " + this.rewardsProgramStates[i].rewardsAssetId.toString() + " for app " + this.appId.toString())
          txns.push(assetOptInTxn)
        }
        
        // farm ops
        params.fee = 1000
        txns.push(makeApplicationNoOpTxnFromObject({
          from: user.address,
          appIndex: this.appId,
          appArgs: [enc.encode(STAKING_STRINGS.farm_ops)],
          suggestedParams: params,
          accounts: undefined,
          foreignAssets: undefined,
          foreignApps: undefined,
          rekeyTo: undefined,
          note: enc.encode("Farm Opts: " + i.toString())
        }))
        
        // claim rewards
        params.fee = 3000
        txns.push(makeApplicationNoOpTxnFromObject({
          from: user.address,
          appIndex: this.appId,
          appArgs: [enc.encode(STAKING_STRINGS.claim_rewards), encodeUint64(i)],
          foreignAssets: [this.rewardsProgramStates[i].rewardsAssetId],
          accounts: [this.rewardsEscrowAccount],
          rekeyTo: undefined,
          foreignApps: [this.votingEscrowAppId || 1],
          suggestedParams: params
        }))
      }
    }
    if (txns.length == 0) {
      return []
    } else if (txns.length == 1) {
      return txns
    } else {
      return assignGroupID(txns)
    }
  }

  async getUpdateTargetUserTxn(user: AlgofiUser): Promise<any> {
    const params = await getParams(this.algod)
    const enc = new TextEncoder()

    params.fee = 2000
    return makeApplicationNoOpTxnFromObject({
      from: user.address,
      appIndex: this.appId,
      appArgs: [enc.encode(STAKING_STRINGS.update_target_user)],
      suggestedParams: params,
      accounts: [user.address],
      foreignAssets: undefined,
      foreignApps: [this.votingEscrowAppId],
      rekeyTo: undefined
    })
  }
  
}
